import { Fragment, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import {
  clearDiscussPropertyState,
  DiscussPropertyRequest,
  discussPropertyStateAction,
  getDiscussPropertyState,
  WeeklySummaryComment,
} from "../../../redux/features/weekly-summary-slice";
import Button from "../../../components/button";
import Input from "../../../components/input";
import { timeAgoWithDate } from "../../../core/utils";
import { CloseOutlined } from "../../../components/icons/close-outlined";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import { getUserState } from "../../../redux/features/auth-slice";

interface Props {
  summaryId: string;
  type: "reason" | "challenge" | "issue" | "goal" | "resource" | null;
  property: any | null;
  setProperty: (value: any) => void;
  onSubmitted: () => void;
}

export default function SummaryPropertyDiscussDialog(props: Props) {
  const { summaryId, type, property, setProperty, onSubmitted } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const discussSummaryState = useSelector(getDiscussPropertyState);

  const cancelButtonRef = useRef(null);

  const [prop, setProp] = useState<{
    type: { id: string; title: string };
    detail: string | null;
    explain: string | null;
    quantity: number | null;
    jobNumber: string | null;
    reason: string | null;
    action: string | null;
    discuss: WeeklySummaryComment[];
  } | null>();

  const handleClose = () => {
    setProperty(null);
    onSubmitted();
    dispatch(clearDiscussPropertyState());
  };

  const submitDiscussProperty = async (values: DiscussPropertyRequest) => {
    await dispatch(discussPropertyStateAction(values));
  };

  useEffect(() => {
    if (property && type) {
      switch (type) {
        case "reason":
          setProp({
            type: { id: property.type.id, title: property.type.title },
            detail: property.detail,
            discuss: property.discuss,
            explain: null,
            quantity: null,
            jobNumber: null,
            reason: null,
            action: null,
          });
          break;
        case "challenge":
          setProp({
            type: { id: property.type.id, title: property.type.title },
            detail: null,
            discuss: property.discuss,
            explain: property.explain,
            quantity: null,
            jobNumber: null,
            reason: null,
            action: property.action,
          });
          break;
        case "issue":
          setProp({
            type: { id: property.type.id, title: property.type.title },
            detail: null,
            discuss: property.discuss,
            explain: null,
            quantity: property.quantity || null,
            jobNumber: property.jobNumber || null,
            reason: property.reason,
            action: property.action,
          });
          break;
        case "goal":
          setProp({
            type: { id: property.goal, title: property.goal },
            detail: null,
            discuss: property.discuss,
            explain: null,
            quantity: null,
            jobNumber: null,
            reason: null,
            action: null,
          });
          break;
        case "resource":
          setProp({
            type: { id: property.type.id, title: property.type.title },
            detail: null,
            discuss: property.discuss,
            explain: property.explain,
            quantity: null,
            jobNumber: null,
            reason: null,
            action: null,
          });
          break;
      }
    } else {
      setProp(null);
    }
  }, [property, type]);

  return (
    <Transition.Root show={property !== null} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 rounded-lg"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="h-4/5 relative transform bg-background-paper text-left shadow-xl transition-all sm:my-4 sm:w-full sm:max-w-lg rounded-lg">
                <div className="p-4 flex justify-between items-center">
                  <h3
                    className="text-base font-semibold leading-6"
                    id="modal-title"
                  >
                    Discuss
                  </h3>
                  <CloseOutlined
                    className="h-5 w-5 cursor-pointer"
                    onClick={handleClose}
                  />
                </div>
                {prop && user && (
                  <div
                    className="flex flex-col px-4 pb-4"
                    style={{
                      height: "calc(100% - 56px)",
                    }}
                  >
                    <div className="flex flex-col border border-divider rounded-lg p-4 space-y-3 mb-2">
                      {prop.type && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            {type === "goal" ? "Goal" : "Type"}
                          </label>
                          <Input
                            type="text"
                            inputType={type === "goal" ? "textarea" : "input"}
                            rows={2}
                            value={prop.type.title}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.detail && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Detail
                          </label>
                          <Input
                            type="text"
                            inputType="textarea"
                            rows={3}
                            value={prop.detail}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.explain && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Explain
                          </label>
                          <Input
                            type="text"
                            inputType="textarea"
                            rows={3}
                            value={prop.explain}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.quantity && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Quantity
                          </label>
                          <Input
                            type="text"
                            value={prop.quantity}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.jobNumber && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Job Number
                          </label>
                          <Input
                            type="text"
                            value={prop.jobNumber}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.reason && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Reason
                          </label>
                          <Input
                            type="text"
                            inputType="textarea"
                            rows={3}
                            value={prop.reason}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                      {prop.action && (
                        <div className="flex items-start">
                          <label className="w-1/4 text-xs text-text-title">
                            Action / Proposed
                          </label>
                          <Input
                            type="text"
                            inputType="textarea"
                            rows={3}
                            value={prop.action}
                            readonly
                            exWrapClassName="w-3/4"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 overflow-y-scroll">
                      {prop.discuss.length > 0 ? (
                        [...prop.discuss]
                          .sort((a, b) => {
                            const timeA = new Date(a.timestamp).getTime();
                            const timeB = new Date(b.timestamp).getTime();
                            return timeB - timeA; // Sort in descending order
                          })
                          .map(
                            (discuss: WeeklySummaryComment, index: number) => (
                              <div
                                key={index}
                                className={`w-full flex flex-col space-y-2 text-sm py-4 ${
                                  index > 0
                                    ? "border-t border-divider border-dashed"
                                    : ""
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <label className="font-medium">
                                    {discuss.user.name}
                                  </label>
                                  <label className="text-xs text-text-title">
                                    {timeAgoWithDate(
                                      new Date(discuss.timestamp)
                                    )}
                                  </label>
                                </div>
                                <label className="max-w-full bg-background-paper text-text-primary break-words">
                                  {discuss.message}
                                </label>
                              </div>
                            )
                          )
                      ) : (
                        <EmptyDataOverlay
                          label="No actions found"
                          exClassName="flex-1"
                        />
                      )}
                    </div>
                    <Formik
                      initialValues={{ comment: "" }}
                      validationSchema={Yup.object().shape({
                        comment: Yup.string().required(),
                      })}
                      onSubmit={(values, { setSubmitting, resetForm }) => {
                        setSubmitting(false);
                        const newDiscusses = [
                          ...prop.discuss,
                          {
                            user: user,
                            message: values.comment,
                            timestamp: new Date(),
                          },
                        ];
                        setProp({
                          ...prop,
                          discuss: newDiscusses,
                        });
                        if (property && type) {
                          submitDiscussProperty({
                            summaryId: summaryId,
                            type: type,
                            propertyId:
                              type === "goal"
                                ? property.goal
                                : property.type.id,
                            discusses: newDiscusses,
                          });
                        }
                        resetForm();
                      }}
                    >
                      {({ values, errors, handleChange, handleSubmit }) => (
                        <Form
                          noValidate
                          autoComplete="off"
                          onSubmit={handleSubmit}
                          className="flex items-center space-x-2 bg-background-paper p-2 border border-divider rounded-lg cursor-pointer mt-2"
                        >
                          <Input
                            id="comment"
                            placeholder="Write an action/proposed..."
                            value={values.comment}
                            onChange={handleChange}
                            exClassName="border-none"
                            exWrapClassName="w-full"
                          />
                          <Button
                            label="Send"
                            exClassName="flex justify-right"
                            loading={discussSummaryState.loading}
                            disabled={
                              Array.isArray(errors) ||
                              Object.values(errors).toString() != ""
                            }
                          />
                        </Form>
                      )}
                    </Formik>
                  </div>
                )}
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
